// Gemeinsame Hilfsfunktionen und Konstanten für den Kunden-Bereich
import { userStorage } from './userStorage';

export const STUFEN = ["keine", "TP", "M", "SM", "MM", "IMM", "VP", "EVP", "P", "SP", "GP", "PP", "CL"];

export const DEFAULT_THEMATIK_OPTIONEN = [
  "Neurodermitis",
  "Diabetes",
  "Gewichtsmanagement",
  "Migräne",
  "Hautirritationen",
  "Bluthochdruck",
  "Arthritis",
  "Schlafstörungen",
  "Verdauungsprobleme",
  "Immunsystem",
  "Energiemangel",
  "Stress & Burnout",
  "Allergien",
  "Gelenkschmerzen",
  "Herzgesundheit",
  "Cholesterin",
  "Schilddrüse",
  "Wechseljahre",
  "Haarausfall",
  "Anti-Aging",
  "Sporternährung",
  "Detox & Entgiftung"
];

export const DEFAULT_VERANSTALTUNG_OPTIONEN = [
  "Infoveranstaltung Live",
  "Vitalabend Call",
  "Plan B Call",
  "BA",
  "WA",
  "PA",
  "Kickoff",
  "Powercamp"
];

// Helper: normalize noch_zu_erledigen from string/null → array
export function normalizeNochZuErledigen(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  if (typeof val === 'string' && val.trim()) {
    return [{ id: `task-legacy-${Date.now()}`, text: val.trim(), done: false, dueDate: null, todoRef: null, createdAt: new Date().toISOString() }];
  }
  return [];
}

// Löscht ein Todo (per ID) aus allen Zielplanungs-Storage-Schlüsseln (täglich/wöchentlich/monatlich/backlog)
export const deleteTodoFromLocalStorage = (todoId) => {
  const keys = ['zielplanung_daily_todos', 'zielplanung_weekly_todos', 'zielplanung_monthly_todos', 'zielplanung_backlog_todos'];
  keys.forEach(key => {
    try {
      const stored = JSON.parse(userStorage.getItem(key));
      if (!stored) return;
      if (Array.isArray(stored)) {
        const filtered = stored.filter(t => t.id !== todoId);
        if (filtered.length !== stored.length) userStorage.setItem(key, JSON.stringify(filtered));
      } else if (typeof stored === 'object') {
        let changed = false;
        const newObj = Object.fromEntries(
          Object.entries(stored).map(([k, arr]) => [k, Array.isArray(arr)
            ? arr.filter(t => { if (t.id !== todoId) return true; changed = true; return false; })
            : arr
          ])
        );
        if (changed) userStorage.setItem(key, JSON.stringify(newObj));
      }
    } catch {}
  });
};

// Löscht alle Kalender-Einträge (Woche/Monat/Jahr) mit matching taskId und triggert Sync-Event
export const deleteCalendarEntriesByTaskId = (taskId) => {
  if (!taskId) return;
  const calendarKeys = [
    'zielplanung_appointments',
    'zielplanung_month_appointments',
    'zielplanung_year_planning',
  ];
  calendarKeys.forEach(key => {
    try {
      const stored = JSON.parse(userStorage.getItem(key));
      if (!stored) return;
      if (Array.isArray(stored)) {
        const filtered = stored.filter(e => e.contactRef?.taskId !== taskId);
        if (filtered.length !== stored.length) userStorage.setItem(key, JSON.stringify(filtered));
      } else if (typeof stored === 'object') {
        let changed = false;
        const newObj = Object.fromEntries(
          Object.entries(stored).map(([k, arr]) => [k, Array.isArray(arr)
            ? arr.filter(e => { if (e.contactRef?.taskId !== taskId) return true; changed = true; return false; })
            : arr
          ])
        );
        if (changed) userStorage.setItem(key, JSON.stringify(newObj));
      }
    } catch {}
  });
  window.dispatchEvent(new CustomEvent('burningteam-calendar-sync'));
};

// Hilfsfunktion: Zähle alle TPs rekursiv in der Downline
export function countTPs(node) {
  let count = 0;
  if (node.typ === "teampartner") {
    count = 1;
  }
  if (node.children && node.children.length > 0) {
    node.children.forEach(child => {
      count += countTPs(child);
    });
  }
  return count;
}

// Berechnung "Nächstes Paket" – datum-erhaltend (alle 3 Monate auf den gleichen Tag)
// statt starre +90 Tage. Komplett TZ-unabhängig: arbeitet nur mit Year/Month/Day-
// Komponenten (UTC-extrahiert), nicht mit Date-Subtraktionen — sonst würden DST-
// Übergänge oder ISO-Strings mit Time-Komponente das Datum um ±1 Tag verschieben.
//
// Beispiel User-Szenario: Einstieg 24.05.2025
// → 24.08.2025 → 24.11.2025 → 24.02.2026 → 24.05.2026 → 24.08.2026 …
//
// @param {Date|string} startDate – Beförderungs-/Einstiegsdatum
// @param {Date} [now=new Date()] – aktuelles Datum (für Tests)
// @returns {{ nextRenewal: Date, completedCycles: number, daysLeft: number }}
export function calculateNextPackage(startDate, now = new Date()) {
  // Schritt 1: Year/Month/Day des Einstiegsdatums extrahieren – TZ-robust.
  // Wenn String "YYYY-MM-DD" → direkt parsen (vermeidet UTC-Interpretation).
  // Wenn ISO mit Zeit oder Date-Objekt → lokale Komponenten nutzen.
  const { y: startY, m: startM, d: startD } = extractYMD(startDate);
  const { y: nowY, m: nowM, d: nowD } = extractYMD(now);

  // Schritt 2: completedCycles berechnen – Wie viele 3-Monats-Zyklen wurden bereits
  // erreicht (heute > Liefertag)?
  // Strikt: Am Liefertag selbst zeigen wir noch das aktuelle Paket ("Heute fällig"),
  // nicht schon das nächste.
  let completedCycles = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const next = ymdAddMonths(startY, startM, startD, (completedCycles + 1) * 3);
    if (compareYMD(next, { y: nowY, m: nowM, d: nowD }) >= 0) break; // next >= heute → Stop
    completedCycles++;
  }

  // Schritt 3: nächstes Paket berechnen
  const nextYMD = ymdAddMonths(startY, startM, startD, (completedCycles + 1) * 3);
  // Date-Objekt für Anzeige – mit lokaler Zeitzone, Mitternacht
  const nextRenewal = new Date(nextYMD.y, nextYMD.m, nextYMD.d, 0, 0, 0, 0);

  // Schritt 4: daysLeft – TZ-unabhängige Tagesdifferenz via Date.UTC
  const utcNext = Date.UTC(nextYMD.y, nextYMD.m, nextYMD.d);
  const utcNow = Date.UTC(nowY, nowM, nowD);
  const daysLeft = Math.round((utcNext - utcNow) / (1000 * 60 * 60 * 24));

  return { nextRenewal, completedCycles, daysLeft };
}

// Extrahiert {y, m, d} aus einer Date|string Eingabe.
// String "YYYY-MM-DD" wird als reines Datum (lokal) interpretiert – KEIN UTC!
// Sonstige Formate (ISO mit Zeit, Date-Objekt) nutzen die lokalen Komponenten.
function extractYMD(input) {
  if (typeof input === 'string') {
    // Match "YYYY-MM-DD" am Anfang
    const dateMatch = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      return { y: parseInt(dateMatch[1], 10), m: parseInt(dateMatch[2], 10) - 1, d: parseInt(dateMatch[3], 10) };
    }
  }
  const d = input instanceof Date ? input : new Date(input);
  return { y: d.getFullYear(), m: d.getMonth(), d: d.getDate() };
}

// Addiert n Monate auf {y, m, d}, mit Clamp auf gültigen Tag im Zielmonat.
// Beispiel: 24.05 + 3M = 24.08 ; 31.01 + 1M = 28.02 (oder 29.02 im Schaltjahr).
function ymdAddMonths(y, m, d, monthsToAdd) {
  const totalMonths = m + monthsToAdd;
  const newYear = y + Math.floor(totalMonths / 12);
  const newMonth = ((totalMonths % 12) + 12) % 12;
  // Letzter gültiger Tag im Zielmonat (aus Date.UTC, TZ-frei)
  const lastDay = new Date(Date.UTC(newYear, newMonth + 1, 0)).getUTCDate();
  return { y: newYear, m: newMonth, d: Math.min(d, lastDay) };
}

// Vergleicht zwei {y,m,d}-Objekte: -1 wenn a<b, 0 gleich, 1 wenn a>b
function compareYMD(a, b) {
  if (a.y !== b.y) return a.y < b.y ? -1 : 1;
  if (a.m !== b.m) return a.m < b.m ? -1 : 1;
  if (a.d !== b.d) return a.d < b.d ? -1 : 1;
  return 0;
}
